import { useState, useEffect } from "react"
import { makeStyles } from "tss-react/mui"
import TextField from "component-lib/Base/components/TextField/TextField"
import Autocomplete from "component-lib/Base/components/Autocomplete"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import { BOOTSTRAP_GRID_MD_MIN } from "constants-lib/dimensions"
import { KEY_CODE_MAPPING } from "constants-lib/keyCodes"
import { getWindow } from "utils-lib/getWindow"
import "./Dropdown.scss"

const ssrWindow = getWindow()

const useStyles = makeStyles()((theme, { inputWidth }) => ({
  root: {
    width: `100%`,
    [theme.breakpoints.up(`sm`)]: {
      width: ssrWindow.innerWidth < BOOTSTRAP_GRID_MD_MIN ? `100%` : `inherit`,
    },
    "& .MuiAutocomplete-endAdornment": {
      right: `0 !important`,
    },
    "& .MuiAutocomplete-endAdornment button": {
      height: `48px`,
      width: `48px`,
    },
  },
  input: {
    backgroundColor: theme.palette.background.white,
    width: inputWidth || `inherit`,
    cursor: `default`,
  },
}))

const Dropdown = ({
  label = ``,
  value = ``,
  inputWidth = ``,
  options = [],
  id = ``,
  handleChange = () => {},
  required = false,
  style = {},
  changeParams = {},
  noDefaultValue = false,
  textFieldProps = {},
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { classes } = useStyles({ inputWidth })

  /* have to do this, as disabling the input disables the dropdown as well, and we don't want the user to be able to clear or type in the input */
  useEffect(() => {
    const inputEl = ssrWindow.document.getElementById(
      id || `select-outlined-${label}`,
    )

    if (inputEl) {
      inputEl.setAttribute(`readonly`, true)
    }
  }, [])

  const handleOnChange = (event, newValue) => {
    handleChange(newValue, changeParams)
    toggleMenu()
  }
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  const closeMenu = () => setIsMenuOpen(false)

  const handleKeyPress = (e) => {
    if (
      e.keyCode === KEY_CODE_MAPPING.ENTER ||
      e.keyCode === KEY_CODE_MAPPING.SPACE_BAR
    ) {
      handleEnterPress(e, toggleMenu())
    }
  }

  const handleEscapePress = (e) => {
    if (e.keyCode === KEY_CODE_MAPPING.ESCAPE) {
      closeMenu()
    }
  }

  return (
    <Autocomplete
      data-testid="MUIDropdown"
      aria-label={label}
      id={id || `select-outlined-${label}`}
      className={classes.root}
      disableClearable
      defaultValue={noDefaultValue ? null : options[0]}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={value}
      onChange={handleOnChange}
      onClick={toggleMenu}
      onBlur={closeMenu}
      onKeyDown={handleEscapePress}
      options={options}
      blurOnSelect
      open={isMenuOpen}
      renderInput={(params) => (
        <TextField
          {...{ ...params, ...textFieldProps }}
          inputProps={{
            "aria-label": label,
            ...(params?.inputProps || {}),
            ...(textFieldProps?.inputProps || {}),
          }}
          className={classes.input}
          style={style}
          label={label}
          required={required}
          variant="outlined"
          autoComplete="new-password"
          onClick={toggleMenu}
          onKeyDown={handleKeyPress}
          onBlur={() => {
            if (typeof textFieldProps.onBlur === "function") {
              textFieldProps.onBlur()
            }

            closeMenu()
          }}
        />
      )}
    />
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Dropdown.propTypes = {
//   label: PropTypes.string,
//   value: PropTypes.string,
//   inputWidth: PropTypes.string,
//   options: PropTypes.array,
//   id: PropTypes.string,
//   handleChange: PropTypes.func,
//   required: PropTypes.bool,
//   style: PropTypes.object,
//   changeParams: PropTypes.object,
//   noDefaultValue: PropTypes.bool,
//   textFieldProps: PropTypes.object,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// Dropdown.defaultProps = {
//   label: ``,
//   value: ``,
//   inputWidth: ``,
//   options: [],
//   id: ``,
//   handleChange: () => {},
//   required: false,
//   style: {},
//   changeParams: {},
//   noDefaultValue: false,
//   textFieldProps: {},
// }

export default Dropdown
